import { ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User } from 'stream-chat';
import { Chat, useCreateChatClient } from 'stream-chat-react';

import itrackStreamI18n from './StreamTranslation';
import { useNotificationPermission } from '@/hooks/useNotificationPermission';
import { useNotificationStore } from '@/stores/notificationstore';
import fetchMissingDispatcherChannels from './MissingDispatcherFetcher';
import registerDevice from './FirebaseConfig';
import { getChatToken } from './StreamChatToken';

const apiKey = import.meta.env.VITE_GETSTREAMIO_APIKEY;

export default function StreamChatLayer({
  userId,
  userName,
  children,
}: {
  userId: string;
  userName: string;
  children: ReactElement;
}) {
  const { t, i18n } = useTranslation();
  const notificationPermission = useNotificationPermission();
  const showNotification = useNotificationStore((state) => state.showNotification);

  const user: User = {
    id: userId,
    name: userName,
    image: `https://getstream.io/random_png/?name=${userName}`,
    language: i18n.language,
  };

  const client = useCreateChatClient({
    apiKey,
    tokenOrProvider: () => getChatToken(userId, apiKey),
    userData: user,
  });

  useEffect(() => {
    async function query() {
      if (client && notificationPermission === 'granted') {
        await registerDevice(client);
      }
      if (!client) return;
      const filter = { members: { $in: [userId] } };
      await client.queryChannels(filter, {}, { watch: true });
    }
    void query();
  }, [client, notificationPermission, userId]);

  useEffect(() => {
    async function showMissingDispChannelsNote() {
      const missingDispChannels = await fetchMissingDispatcherChannels(client!);
      if (!missingDispChannels) {
        return;
      }
      showNotification('info', [t('streamChat.custom.missingDispatcher'), missingDispChannels].join('\n'));
    }
    if (!client) return;
    void showMissingDispChannelsNote();
  }, [client]);

  if (!client) return children;
  const streami18n = itrackStreamI18n(t, i18n);

  return (
    <Chat client={client} i18nInstance={streami18n}>
      {children}
    </Chat>
  );
}
